import { graphql, PageProps } from "gatsby";
import React, { FC, useMemo } from "react";

import { CookiesDeclaration } from "../../cookies-declaration/cookies-declaration";
import { EventDetail } from "../../events/event-detail";
import { ExhibitionFinderSection } from "../../exhibition-finder";
import { RequestInfoFormSection } from "../../forms/request-info-form/form-section";
import { JobDetail } from "../../jobs/job-detail";
import { WhatsNextFinderSection } from "../../whats-next-finder";
import { CustomerStoriesGridSection } from "../customer-stories-grid-section";
import { StoreLocatorSection } from "../store-locator/store-locator-section";
import { SpecialContentHandler } from "./types";

export interface SpecialContentProps extends Queries.SpecialContentDataFragment {
  location: PageProps["location"];
}

export const SpecialContent: FC<SpecialContentProps> = ({
  handler,
  node_locale,
  location,
  whatsNextItems: _whatsNextItems,
  exhibitionsItems: _exhibitionsItems,
  storesItems: _storesItems,
  customerStoriesItems: _customerStoriesItems,
}) => {
  const whatsNextItems = useMemo(() => {
    return (_whatsNextItems ?? []).filter(Boolean) as Queries.WhatsNextDataFragment[];
  }, [_whatsNextItems]);

  const exhibitionsItems = useMemo(() => {
    return (_exhibitionsItems ?? []).filter(Boolean) as Queries.ExhibitionCardDataFragment[];
  }, [_exhibitionsItems]);

  const storesItems = useMemo(() => {
    return (_storesItems ?? []).filter(Boolean) as Queries.CompanySiteDetailDataFragment[];
  }, [_storesItems]);

  const customerStoriesItems = useMemo(() => {
    return (_customerStoriesItems ?? []).filter(Boolean) as Queries.CustomerStoryCardDataFragment[];
  }, [_customerStoriesItems]);

  switch (handler) {
    case SpecialContentHandler.JobDetail:
      return <JobDetail location={location} locale={node_locale} />;
    case SpecialContentHandler.NewsletterForm:
    case SpecialContentHandler.RequestDemoForm:
    case SpecialContentHandler.RequestSupportForm:
    case SpecialContentHandler.RequestInfoForm:
      return <RequestInfoFormSection lang={node_locale} type={handler} />;
    case SpecialContentHandler.CustomerStoriesList:
      return <CustomerStoriesGridSection items={customerStoriesItems} />;
    case SpecialContentHandler.ExhibitionFinder:
      return <ExhibitionFinderSection items={exhibitionsItems} />;
    case SpecialContentHandler.WhatsNextFinder:
      return <WhatsNextFinderSection items={whatsNextItems} />;
    case SpecialContentHandler.StoreLocator:
      return <StoreLocatorSection items={storesItems} />;
    case SpecialContentHandler.EventDetail:
      return <EventDetail location={location} />;
    case SpecialContentHandler.CookieDeclaration:
      return <CookiesDeclaration />;
    default:
      return null;
  }
};

export const query = graphql`
  fragment SpecialContentData on ContentfulSpecialContent {
    handler
    node_locale
    whatsNextItems {
      ...WhatsNextData
    }
    exhibitionsItems {
      ...ExhibitionCardData
    }
    storesItems {
      ...CompanySiteDetailData
    }
    customerStoriesItems {
      ...CustomerStoryCardData
    }
  }
`;
